import { INSURANCE_POLICY_HOLDER_RELATIONSHIPS } from "constants/insurance-policies";
import { BAS } from "constants/patients";
import { InsurancePolicy } from "services/types/insurance-policies.type";
import { formatPhoneNumber } from "utils/common";

import { PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS } from "../merge-tags-options/patient-primary-insurance-policy.utils";
import { getFormattedDate } from "../merge-tags.utils";
import { MergeTagsReplaceHandler } from "./merge-tags-values";

export const PATIENT_PRIMARY_INSURANCE_POLICY_VALUES: MergeTagsReplaceHandler =
  {
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_CARRIER]:
      ({ data }) => {
        return data.patient?.primary_insurance_policy?.insurer?.name;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_MEMBER_ID]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.policyholder_member_id;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_GROUP_NUMBER]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.policyholder_group_number;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_PLAN]: ({
      data
    }) => {
      const insurance = data.patient
        ?.primary_insurance_policy as unknown as InsurancePolicy;

      return insurance?.plan_name;
    },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_COMPANY_CONTACT_NUMBER]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return formatPhoneNumber(insurance?.insurer_phone_number ?? "");
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_RELATION_TO_ISSUER]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return INSURANCE_POLICY_HOLDER_RELATIONSHIPS.getFromCode(
          insurance?.policyholder_relationship
        );
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_NOTES]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.notes;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_FIRST_NAME]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.policyholder_first_name;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_LAST_NAME]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.policyholder_last_name;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_SEX]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return BAS.getFromCode(insurance?.policyholder_sex);
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_DATE_OF_BIRTH]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return getFormattedDate(insurance?.policyholder_birthdate);
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_EMPLOYER_NAME]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.policyholder_employer_name;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_FULL_ADDRESS]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return [
          insurance?.policyholder_street_address_1,
          insurance?.policyholder_street_address_2,
          insurance?.policyholder_city,
          `${insurance?.policyholder_state || ""} ${
            insurance?.policyholder_zip_code || ""
          }`.trim()
        ]
          .filter(Boolean)
          .join(", ");
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_ADDRESS_LINE1]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.policyholder_street_address_1;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_ADDRESS_LINE2]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.policyholder_street_address_2;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_CITY]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.policyholder_city;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_STATE]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.policyholder_state;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_ZIP_CODE]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.policyholder_zip_code;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_HOLDER_PHONE_NUMBER]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return formatPhoneNumber(insurance?.policyholder_phone ?? "");
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_BENEFITS_HEARING_AID_BENEFIT]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.hearing_benefit
          ? String(insurance?.hearing_benefit)
          : null;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_BENEFITS_DEDUCTIBLE]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.deductible ? String(insurance?.deductible) : null;
      },
    [PATIENT_PRIMARY_INSURANCE_POLICY_OPTIONS.PRIMARY_INSURANCE_POLICY_COPAY]:
      ({ data }) => {
        const insurance = data.patient
          ?.primary_insurance_policy as unknown as InsurancePolicy;

        return insurance?.copay ? String(insurance?.copay) : null;
      }
  };
